import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Building,
  Film,
  Theater,
  Package,
  CheckCircle,
  Loader2,
  Sparkles,
  Crown,
  Star,
  Rocket,
  Heart,
  Trophy,
  Camera,
  Zap,
  PlayCircle,
  Users,
  Target,
} from 'lucide-react';

type PartnerType = 'studio' | 'theater' | 'independent' | 'distributor';

interface PartnerOption {
  type: PartnerType;
  title: string;
  description: string;
  icon: typeof Building;
  features: string[];
}

const PARTNER_OPTIONS: PartnerOption[] = [
  {
    type: 'studio',
    title: 'Production Studio',
    description:
      'Create cinematic magic and bring blockbuster dreams to life through slice funding',
    icon: Film,
    features: [
      'Launch epic slice campaigns',
      'Theater premiere guarantees', 
      'Professional creator dashboard',
      'Industry partnership network',
    ],
  },
  {
    type: 'independent',
    title: 'Independent Creator',
    description:
      'Turn your unique vision into funded reality with the power of slice donations',
    icon: Camera,
    features: [
      'Fund passion projects easily',
      'Build devoted fan communities',
      'Keep 100% creative control',
      'Direct audience connection',
    ],
  },
  {
    type: 'theater',
    title: 'Theater Partner',
    description:
      'Transform your screens into premiere destinations for crowdfunded content',
    icon: Theater,
    features: [
      'Exclusive screening rights',
      'Revenue sharing bonuses',
      'VIP premiere events',
      'Marketing co-op programs',
    ],
  },
  {
    type: 'distributor',
    title: 'Content Distributor',
    description: 'Scale the slice revolution by distributing multiple creator campaigns',
    icon: Package,
    features: [
      'Multi-creator management',
      'Advanced API access',
      'Bulk campaign tools',
      'Enterprise analytics suite',
    ],
  },
];

export default function PartnerTypeSelection() {
  const navigate = useNavigate();
  const { user, refreshPublisher } = useAuth();
  const [selectedType, setSelectedType] = useState<PartnerType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [hoveredCard, setHoveredCard] = useState<PartnerType | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);

  const handleSubmit = async () => {
    if (!selectedType || !user) {
      console.log('❌ Missing selectedType or user');
      console.log('selectedType:', selectedType);
      console.log('user:', user);
      return;
    }

    console.log('🔵 Starting partner type submission:', selectedType);
    console.log('🔵 User ID:', user.id);
    console.log('🔵 User Email:', user.email);
    setIsSubmitting(true);
    setError('');

    try {
      console.log('🔵 Creating publisher record...');

      // First check if record already exists with timeout
      console.log('🔵 Checking for existing publisher record...');
      const checkPromise = supabase
        .from('publishers')
        .select('*')
        .eq('auth_user_id', user.id)
        .maybeSingle();

      const checkTimeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Database query timeout')), 10000)
      );

      const checkResult = await Promise.race([
        checkPromise,
        checkTimeoutPromise,
      ]).catch(err => {
        console.error('❌ Check query failed or timed out:', err);
        throw err;
      });

      const { data: existing, error: checkError } = checkResult as any;

      console.log('🔵 Existing publisher check result:', existing);
      if (checkError) {
        console.warn('⚠️ Check error:', checkError);
      }

      if (existing) {
        console.log('⚠️ Publisher record already exists, navigating to onboarding');
        navigate('/onboarding');
        return;
      }

      // Create initial publisher record with selected partner type
      console.log('🔵 Inserting new publisher record with data:', {
        auth_user_id: user.id,
        business_type: selectedType,
        contact_email: user.email || '',
        company_name: '',
        legal_entity_name: '',
        onboarding_status: 'pending_application',
        is_active: false,
        is_verified: false,
      });

      const insertPromise = supabase
        .from('publishers')
        .insert({
          auth_user_id: user.id,
          business_type: selectedType,
          contact_email: user.email || '',
          company_name: '',
          legal_entity_name: '',
          is_active: false,
          is_verified: false,
        })
        .select()
        .single();

      const insertTimeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Insert query timeout')), 10000)
      );

      const insertResult = await Promise.race([
        insertPromise,
        insertTimeoutPromise,
      ]).catch(err => {
        console.error('❌ Insert query failed or timed out:', err);
        throw err;
      });

      const { data, error: insertError } = insertResult as any;

      if (insertError) {
        console.error('❌ Insert error details:', {
          message: insertError.message,
          details: insertError.details,
          hint: insertError.hint,
          code: insertError.code,
        });
        throw insertError;
      }

      console.log('✅ Publisher record created successfully:', data);
      console.log('🔵 Refreshing publisher data in auth context...');

      // Refresh with timeout to prevent infinite loading
      const refreshPromise = refreshPublisher();
      const refreshTimeoutPromise = new Promise((resolve) =>
        setTimeout(resolve, 2000)
      );

      await Promise.race([refreshPromise, refreshTimeoutPromise]);

      console.log('✅ Publisher data refreshed (or timed out after 2s)');
      // Trigger celebration before navigation
      setShowCelebration(true);
      
      setTimeout(() => {
        console.log('🔵 Navigating to /onboarding...');
        navigate('/onboarding');
      }, 2500);

      console.log('✅ Navigation complete');
    } catch (err: any) {
      console.error('❌ Error in handleSubmit:', err);
      console.error('❌ Error message:', err.message);
      console.error('❌ Error details:', err.details);
      console.error('❌ Error hint:', err.hint);
      console.error('❌ Error code:', err.code);
      setError(err.message || 'Failed to save partner type. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-background'>
        <Card className='w-full max-w-md'>
          <CardHeader className='text-center'>
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>Please sign in to continue.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/auth')} className='w-full'>
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-background via-muted/20 to-background py-12 relative overflow-hidden'>
      {/* Floating background elements */}
      <div className='pointer-events-none absolute inset-0'>
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className='absolute h-1 w-1 rounded-full bg-primary/20 animate-float'
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 6}s`,
              animationDuration: `${4 + Math.random() * 4}s`,
            }}
          />
        ))}
      </div>
      
      {/* Success celebration overlay */}
      {showCelebration && (
        <div className='pointer-events-none absolute inset-0 z-50 flex items-center justify-center bg-background/80'>
          <div className='text-center animate-fade-in-up'>
            <div className='mb-4 flex justify-center'>
              <div className='flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-r from-primary to-accent shadow-glow animate-float'>
                <Crown className='h-12 w-12 text-white animate-bounce' />
              </div>
            </div>
            <h2 className='text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mb-2'>
              Welcome to Kinora Family!
            </h2>
            <p className='text-lg text-muted-foreground'>
              Your creative journey starts now...
            </p>
            {/* Celebration particles */}
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className='absolute h-2 w-2 rounded-full animate-bounce'
                style={{
                  left: `${30 + Math.random() * 40}%`,
                  top: `${30 + Math.random() * 40}%`,
                  backgroundColor: ['#8b5cf6', '#ec4899', '#06d6a0', '#ffd166'][i % 4],
                  animationDelay: `${Math.random() * 0.8}s`,
                  animationDuration: '1.5s',
                }}
              />
            ))}
          </div>
        </div>
      )}
      
      <div className='mx-auto max-w-6xl px-4 relative z-10'>
        {/* Enhanced Header */}
        <div className='mb-12 text-center animate-fade-in-up'>
          <div className='mb-6 flex justify-center'>
            <div className='flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-primary to-accent shadow-glow animate-float'>
              <Target className='h-8 w-8 text-white' />
            </div>
          </div>
          <h1 className='mb-4 text-4xl font-bold bg-gradient-to-r from-primary via-accent to-business-500 bg-clip-text text-transparent'>
            Choose Your Creative Path
          </h1>
          <p className='text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed'>
            Every great creator story starts with a single choice. Pick the path that matches your creative ambitions and let's build something amazing together.
          </p>
          
          {/* Progress indicator */}
          <div className='mt-8 flex justify-center items-center gap-2'>
            <div className='flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white text-sm font-medium'>
              <CheckCircle className='h-4 w-4' />
            </div>
            <div className='h-0.5 w-8 bg-primary' />
            <div className='flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white text-sm font-medium animate-pulse'>
              2
            </div>
            <div className='h-0.5 w-8 bg-muted' />
            <div className='flex h-8 w-8 items-center justify-center rounded-full border-2 border-muted text-muted-foreground text-sm font-medium'>
              3
            </div>
            <div className='ml-4 text-sm text-muted-foreground'>
              Step 2 of 3: Choose Your Role
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert className='mb-6 border-destructive/50 bg-destructive/15'>
            <AlertDescription className='text-destructive'>
              {error}
            </AlertDescription>
          </Alert>
        )}

        {/* Enhanced Partner Type Cards */}
        <div className='mb-8 grid gap-6 md:grid-cols-2'>
          {PARTNER_OPTIONS.map((option, index) => {
            const Icon = option.icon;
            const isSelected = selectedType === option.type;
            const isHovered = hoveredCard === option.type;

            return (
              <Card
                key={option.type}
                className={`cursor-pointer transition-all duration-300 animate-fade-in-up ${
                  isSelected
                    ? 'border-primary bg-gradient-to-br from-primary/15 to-accent/10 shadow-glow ring-2 ring-primary scale-105'
                    : isHovered
                    ? 'border-accent/50 shadow-medium hover:shadow-glow'
                    : 'hover:border-muted-foreground hover:shadow-lg'
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => {
                  setSelectedType(option.type);
                  // Add a little celebration when selected
                  if (!isSelected) {
                    // Trigger a micro-celebration
                    const card = document.querySelector(`[data-card="${option.type}"]`);
                    card?.classList.add('animate-bounce');
                    setTimeout(() => card?.classList.remove('animate-bounce'), 600);
                  }
                }}
                onMouseEnter={() => setHoveredCard(option.type)}
                onMouseLeave={() => setHoveredCard(null)}
                data-card={option.type}
              >
                <CardHeader className='relative'>
                  {/* Animated background gradient */}
                  {(isSelected || isHovered) && (
                    <div className='absolute inset-0 bg-gradient-to-r from-primary/10 via-accent/10 to-business-500/10 opacity-50 animate-gradient-x rounded-t-xl' />
                  )}
                  
                  <div className='mb-4 flex items-center justify-between relative z-10'>
                    <div className={`flex h-12 w-12 items-center justify-center rounded-full transition-all duration-300 ${
                      isSelected 
                        ? 'bg-gradient-to-r from-primary to-accent shadow-glow scale-110' 
                        : isHovered
                        ? 'bg-primary/20 scale-110'
                        : 'bg-primary/10'
                    }`}>
                      <Icon className={`h-6 w-6 transition-all duration-300 ${
                        isSelected 
                          ? 'text-white animate-bounce' 
                          : isHovered
                          ? 'text-primary scale-110'
                          : 'text-primary'
                      }`} />
                    </div>
                    {isSelected && (
                      <div className='flex items-center gap-2'>
                        <Sparkles className='h-5 w-5 text-accent animate-spin' style={{ animationDuration: '3s' }} />
                        <CheckCircle className='h-6 w-6 text-primary animate-bounce' />
                      </div>
                    )}
                  </div>
                  <CardTitle className={`text-xl transition-all duration-300 ${
                    isSelected 
                      ? 'bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent' 
                      : ''
                  }`}>
                    {option.title}
                  </CardTitle>
                  <CardDescription className={`text-sm transition-all duration-300 ${
                    isSelected || isHovered ? 'text-foreground' : ''
                  }`}>
                    {option.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className='relative z-10'>
                  <ul className='space-y-3'>
                    {option.features.map((feature, featureIndex) => {
                      const icons = [Star, Heart, Zap, Trophy];
                      const FeatureIcon = icons[featureIndex % icons.length];
                      
                      return (
                        <li
                          key={featureIndex}
                          className={`flex items-center gap-3 text-sm transition-all duration-300 group/feature ${
                            isSelected || isHovered ? 'text-foreground' : 'text-muted-foreground'
                          }`}
                          style={{ animationDelay: `${featureIndex * 0.1}s` }}
                        >
                          <div className={`flex h-5 w-5 items-center justify-center rounded-full transition-all duration-300 ${
                            isSelected 
                              ? 'bg-gradient-to-r from-primary to-accent shadow-sm' 
                              : isHovered
                              ? 'bg-primary/20'
                              : 'bg-primary/10'
                          }`}>
                            <FeatureIcon className={`h-2.5 w-2.5 transition-all duration-300 ${
                              isSelected 
                                ? 'text-white' 
                                : 'text-primary'
                            }`} />
                          </div>
                          <span className='group-hover/feature:translate-x-1 transition-transform duration-200'>
                            {feature}
                          </span>
                          {isSelected && (
                            <div className='ml-auto'>
                              <CheckCircle className='h-3 w-3 text-green-500 animate-fade-in' />
                            </div>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Enhanced Continue Button */}
        <div className='flex flex-col items-center gap-4'>
          {selectedType && (
            <div className='text-center animate-fade-in-up'>
              <p className='text-sm text-muted-foreground mb-2'>
                Great choice! You've selected:
              </p>
              <div className='inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20'>
                {(() => {
                  const selectedOption = PARTNER_OPTIONS.find(opt => opt.type === selectedType);
                  const Icon = selectedOption?.icon || Users;
                  return (
                    <>
                      <Icon className='h-4 w-4 text-primary' />
                      <span className='text-sm font-medium text-primary'>
                        {selectedOption?.title}
                      </span>
                      <Sparkles className='h-4 w-4 text-accent animate-pulse' />
                    </>
                  );
                })()}
              </div>
            </div>
          )}
          
          <Button
            size='lg'
            onClick={handleSubmit}
            disabled={!selectedType || isSubmitting}
            className={`min-w-[250px] transition-all duration-300 ${
              selectedType
                ? 'bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-glow'
                : 'bg-muted text-muted-foreground cursor-not-allowed'
            } group relative overflow-hidden`}
          >
            <div className='absolute inset-0 bg-gradient-to-r from-accent/20 to-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300' />
            {isSubmitting ? (
              <div className='flex items-center gap-2 relative z-10'>
                <div className='relative'>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  <div className='absolute inset-0'>
                    <Sparkles className='h-4 w-4 animate-pulse text-white/50' />
                  </div>
                </div>
                <span>Setting Up Your Creative Empire...</span>
                <Crown className='h-4 w-4 animate-bounce' style={{ animationDelay: '0.2s' }} />
              </div>
            ) : selectedType ? (
              <div className='flex items-center gap-2 relative z-10'>
                <Rocket className='h-4 w-4 group-hover:translate-x-1 transition-transform duration-300' />
                <span>Begin My Kinora Journey</span>
                <Star className='h-4 w-4 group-hover:animate-spin transition-transform duration-300' />
              </div>
            ) : (
              <div className='flex items-center gap-2 relative z-10'>
                <Target className='h-4 w-4' />
                <span>Choose Your Path First</span>
              </div>
            )}
          </Button>
          
          {!selectedType && (
            <p className='text-xs text-muted-foreground text-center animate-fade-in'>
              Pick the role that best describes your creative ambitions above
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
